import { Button } from '@ctw/shared/components/button';
import { TelemetryBoundary } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import type { CdaDocument } from '@ctw/shared/utils/cda/types';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faChevronDown, faChevronUp, faSearch, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { compact, debounce, escapeRegExp, isEmpty, isObject } from 'lodash-es';
import {
  Fragment,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface CdaSearchInputProps {
  cdaDocument: CdaDocument;
  className?: ClassName;
  minInputLength?: number;
  name?: string;
}

export const CdaSearchInput = ({
  cdaDocument,
  className,
  name = 'cda-search-input',
  minInputLength = 2,
}: CdaSearchInputProps) => {
  const [searchQuery, internalSetSearchQuery] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchResults, setSearchResults] = useState<Array<SearchResult>>([]);
  const [selectedSearchResult, internalSetSelectedSearchResult] = useState<SearchResult | null>(
    null,
  );

  const ref = useRef<HTMLInputElement>(null);
  const { width } = useContainerQuery({ containerRef: ref });
  const inputFontSize = useMemo(() => {
    if (ref.current) {
      const computedInputStyle = getComputedStyle(ref.current);
      const fontSize = computedInputStyle.fontSize;
      return Number.parseInt(fontSize, 10);
    }
    return 14;
  }, []);

  const options = useMemo(
    () =>
      searchResults.map((result) => ({
        value: result,
        label: `${result.beforeText}${result.matchText}${result.afterText}`,
        indexId: result.indexId,
        key: `${result.indexId}-from-${result.position.start}-${result.position.end}-${selectedSearchResult?.indexId ?? 'no_selected_index'}`,
      })),
    [searchResults, selectedSearchResult],
  );

  const setSearchQuery = useCallback(
    (query: string | null) => internalSetSearchQuery(isEmpty(query) ? null : query),
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigateToResult intentionally omitted to keep setSelectedSearchResult stable across renders
  const setSelectedSearchResult = useCallback((searchResult: SearchResult | null) => {
    if (searchResult) {
      internalSetSelectedSearchResult(searchResult);
      navigateToResult(searchResult);

      setTimeout(() => {
        const ranges = rangesForSearchResults([searchResult]);

        const highlight = new Highlight(...ranges);
        CSS.highlights.set('selected-search-result', highlight);
      }, 100);
    } else {
      internalSetSelectedSearchResult(null);
      CSS.highlights.delete('selected-search-result');
    }
  }, []);

  const navigateToResult = useCallback((result: SearchResult) => {
    document.querySelector(`[data-search-index='${result.indexId}']`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start',
    });

    setTimeout(() => {
      document
        .querySelector(`[data-combobox-option-index-id="${result.indexId}"]`)
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, 0);
  }, []);

  const previousResult = useCallback(() => {
    if (selectedSearchResult) {
      const previousIndex = selectedSearchResult.humanIndex - 2; /* 1-based, subtract two */
      const previousSearchResult =
        previousIndex < 0
          ? searchResults.at(searchResults.length - 1)
          : (searchResults.at(previousIndex) ?? searchResults.at(0) ?? null);
      if (previousSearchResult) {
        setSelectedSearchResult(previousSearchResult);
      }
    }
  }, [searchResults, selectedSearchResult, setSelectedSearchResult]);

  const nextResult = useCallback(() => {
    if (selectedSearchResult) {
      const nextIndex = selectedSearchResult.humanIndex; /* 1-based, already next index */
      const nextSearchResult = searchResults.at(nextIndex) ?? searchResults.at(0) ?? null;
      if (nextSearchResult) {
        setSelectedSearchResult(nextSearchResult);
      }
    }
  }, [searchResults, selectedSearchResult, setSelectedSearchResult]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: include `width` because re-renders reset CSS highlights
  useEffect(() => {
    const ranges = rangesForSearchResults(searchResults);

    const highlight = new Highlight(...ranges);
    CSS.highlights.set('search-result', highlight);
  }, [searchResults, width]);

  const renderCustomOption = useCallback(
    (option: ComboxboxFieldOption) => {
      const tempCanvas = document.createElement('canvas');
      const tempCanvasContext = tempCanvas.getContext('2d');
      if (tempCanvasContext) {
        tempCanvasContext.font = `${inputFontSize}px Inter`;
      }
      const characterWidth = (tempCanvasContext?.measureText('A')?.width ?? 10) * 0.8;

      const result = option.value as SearchResult;
      const surroundingCharacterCount = Math.round(
        width / characterWidth - result.matchText.length,
      );
      const afterCharacterCount = Math.min(surroundingCharacterCount / 2, result.afterText.length);
      const beforeCharacterCount = Math.min(
        surroundingCharacterCount - afterCharacterCount,
        result.beforeText.length,
      );

      const needsAfterTruncation =
        result.afterText.length > afterCharacterCount && result.afterText.length > 0;
      const needsBeforeTruncation =
        result.beforeText.length > beforeCharacterCount && result.beforeText.length > 0;

      return (
        <ComboboxOptionContent
          option={{
            value: result,
            label: result.matchText,
            isSelected: selectedSearchResult?.indexId === result.indexId,
            indexId: result.indexId,
            onClick: () => setSelectedSearchResult(result),
            render: (
              <>
                {needsBeforeTruncation && <span>&hellip;</span>}
                {result.beforeText.slice(
                  result.beforeText.length - beforeCharacterCount,
                  result.beforeText.length,
                )}
                <mark className={tw`bg-transparent font-semibold`}>{result.matchText}</mark>
                {result.afterText.slice(0, afterCharacterCount)}
                {needsAfterTruncation && <span>&hellip;</span>}
                <div className={tw`text-content-subtle text-xs`}>{result.sectionName}</div>
              </>
            ),
          }}
        />
      );
    },
    [setSelectedSearchResult, selectedSearchResult, inputFontSize, width],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: clearSearch intentionally omitted to keep handleSearch stable across renders
  const handleSearch = useCallback(
    (newSearchTerm: string) => {
      if (newSearchTerm.length >= minInputLength) {
        CSS.highlights.clear();

        setIsLoading(true);
        const newSearchResults = performDocumentSearch({ cdaDocument, searchTerm: newSearchTerm });
        setSearchResults(newSearchResults);
        const newSelectedSearchResult = newSearchResults.length > 0 ? newSearchResults[0] : null;
        setSelectedSearchResult(newSelectedSearchResult);
        setIsLoading(false);

        setSearchQuery(newSearchTerm);
      } else {
        clearSearch();
      }
    },
    [cdaDocument, setSearchQuery, setSelectedSearchResult, minInputLength],
  );

  // Delay handle search input so that we don't fire a bunch of events until the user has had time to type.
  const debouncedSearchInputChange = useMemo(
    () => debounce(handleSearch, 250, { leading: false, trailing: true }),
    [handleSearch],
  );

  const onSelectChange = (e: unknown) => {
    const option = e as ComboxboxFieldOption | undefined;

    if (option?.value && isSearchResult(option.value)) {
      setSearchQuery(option.label);
      setSelectedSearchResult(option.value);
    }
  };

  const clearSearch = useCallback(() => {
    setSearchQuery(null);
    setSearchResults([]);
    setSelectedSearchResult(null);
  }, [setSearchQuery, setSelectedSearchResult]);

  const handleSearchHotkeys = useCallback((event: KeyboardEvent | ReactKeyboardEvent) => {
    const isCmdOrCtrlF = (event.ctrlKey || event.metaKey) && event.key === 'f';
    const isEnter = event.key === 'Enter';

    if (isCmdOrCtrlF || isEnter) {
      event.preventDefault();

      inputRef.current?.focus();

      if (isEnter && !event.shiftKey) {
        (document.getElementById('cda-search-input-next-result') as HTMLElement | null)?.click();
      }

      if (isEnter && event.shiftKey) {
        (
          document.getElementById('cda-search-input-previous-result') as HTMLElement | null
        )?.click();
      }
    }
  }, []);

  useEffect(() => {
    // When a dialog is active, attach listeners to the dialog to avoid the dialog consuming events
    // and to avoid performing actions outside the dialog
    const hotkeyListenerElement = (document.querySelector('[role="dialog"]') ??
      document) as HTMLElement;

    hotkeyListenerElement.addEventListener('keydown', handleSearchHotkeys);

    return () => hotkeyListenerElement.removeEventListener('keydown', handleSearchHotkeys);
  }, [handleSearchHotkeys]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only source from URL on initial render
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const urlSearchQuery = urlSearchParams.get('documentSearchQuery');

    if (urlSearchQuery !== null && !isEmpty(urlSearchQuery)) {
      // Defer application of URL search query until document has time to render
      setTimeout(() => {
        handleSearch(urlSearchQuery);
      }, 500);
    }
  }, []);

  return (
    <TelemetryBoundary boundaryName="SearchCombobox" initialContext={{ searchComboboxName: name }}>
      {({ telemetry }) => (
        <Combobox
          onChange={(e) => {
            const option = e as unknown as ComboxboxFieldOption | undefined;

            if (option?.value) {
              telemetry.trackInteraction('search_combobox_result_selected', {
                searchComboboxIndexId: option.key,
              });
            }
            onSelectChange(e);
          }}
          multiple={
            /* HeadlessUI always closes singular comboboxes on item selection, so use a multi-select
            even though it will only ever be a single value */
            true
          }
          value={[searchQuery]}
          immediate={true}
        >
          <style>
            {`
              ::highlight(search-result) {
                background-color: #feff05;
                border-radius: 0.35rem;
              }
              ::highlight(selected-search-result) {
                background-color: #ff9631;
                border-radius: 0.35rem;
              }
            `}
          </style>
          <div className={twx('relative flex items-center gap-2', className)} ref={ref}>
            <div className={tw`flex flex-1 items-center gap-2`}>
              <div className={tw`relative grow`}>
                <div
                  className={tw`pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2`}
                >
                  <FontAwesomeIcon icon={faSearch} className={tw`h-4 w-4 text-content-icon`} />
                </div>
                <ComboboxInput
                  ref={inputRef}
                  value={searchQuery ?? ''}
                  className={twx('listbox-input w-full flex-1 bg-white pr-20 pl-7', className)}
                  onFocus={() => telemetry.trackInteraction('search_combobox_focus')}
                  onBlur={(event) => {
                    // Prevent default and stop propagation to keep combobox open when clicking next/previous buttons
                    event.preventDefault();
                    event.stopPropagation();
                    telemetry.trackInteraction('search_combobox_unfocus');
                  }}
                  onKeyDown={(event: ReactKeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter' && (options.length === 0 || isLoading)) {
                      event.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    // Due to debounce, we have to persist the event.
                    // https://reactjs.org/docs/legacy-event-pooling.html
                    e.persist();
                    setSearchQuery(e.target.value);
                    debouncedSearchInputChange(e.target.value);
                  }}
                />
              </div>
              <div
                className={twx(
                  'absolute right-2 z-navigation-sticky flex items-center gap-1 opacity-100 transition-all duration-150',
                  {
                    'pointer-events-none opacity-0': searchQuery === null,
                  },
                )}
              >
                {searchResults.length > 0 && (
                  <div
                    data-testid="cda-search-input-ticker"
                    className={tw`font-bold text-content-subtle text-xs`}
                  >
                    {selectedSearchResult?.humanIndex ?? 1}/{searchResults.length}
                  </div>
                )}
                <Button
                  id="cda-search-input-previous-result"
                  testId="cda-search-input-previous-result"
                  type="button"
                  ariaLabel="Previous Result"
                  variant="circular"
                  size="xs"
                  className={tw`flex items-center justify-center bg-transparent p-1 text-content-subtle shadow-none hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
                  onClick={() => {
                    // Keep combobox input focused when clicking this control button
                    inputRef.current?.focus();
                    previousResult();
                  }}
                >
                  <FontAwesomeIcon icon={faChevronUp} className={tw`h-3 w-3`} />
                </Button>
                <Button
                  id="cda-search-input-next-result"
                  testId="cda-search-input-next-result"
                  type="button"
                  ariaLabel="Next Result"
                  variant="circular"
                  size="xs"
                  className={tw`flex items-center justify-center bg-transparent p-1 text-content-subtle shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
                  onClick={() => {
                    // Keep combobox input focused when clicking this control button
                    inputRef.current?.focus();
                    nextResult();
                  }}
                >
                  <FontAwesomeIcon icon={faChevronDown} className={tw`h-3 w-3`} />
                </Button>
                <Button
                  testId="cda-search-input-clear"
                  type="button"
                  ariaLabel="Clear Search"
                  variant="circular"
                  size="xs"
                  className={tw`flex items-center justify-center bg-transparent p-1 text-content-subtle shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
                  onClick={() => {
                    // Unlike the next/prev buttons above, allow the combobox to unfocus and close when
                    // clicking this control button
                    clearSearch();
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} className={tw`h-3 w-3`} />
                </Button>
              </div>
            </div>
            <input
              hidden={true}
              name={name}
              value={selectedSearchResult === null ? '' : JSON.stringify(selectedSearchResult)}
              readOnly={true}
            />
            <ComboboxOptions
              modal={false}
              className={tw`listbox absolute top-9 z-10 m-0 mt-1 max-h-60 w-full list-none overflow-auto rounded-md bg-white p-0 py-1 text-base shadow-lg ring-1 ring-opacity-5 focus:outline-none sm:text-sm`}
            >
              <ComboboxOptionsContent
                isLoading={isLoading}
                options={options}
                query={searchQuery}
                renderCustomOption={renderCustomOption}
                minInputLength={minInputLength}
              />
            </ComboboxOptions>
          </div>
        </Combobox>
      )}
    </TelemetryBoundary>
  );
};

interface ComboxboxFieldOption {
  value: SearchResult | null;
  indexId?: string;
  label: string;
  render?: ReactNode;
  key?: string;
}

type ComboboxOptionsContentProps = {
  isLoading: boolean;
  options: Array<ComboxboxFieldOption>;
  query: string | null;
  renderCustomOption?: (option: ComboxboxFieldOption) => ReactNode;
  minInputLength: number;
};

const ComboboxOptionsContent = ({
  options,
  query,
  isLoading,
  renderCustomOption,
  minInputLength,
}: ComboboxOptionsContentProps) => {
  if (query === null || query.length === 0) {
    return <ComboboxOptionContent option={{ value: null, label: 'Type to search' }} />;
  }

  if (isLoading) {
    return <ComboboxOptionContent option={{ value: null, label: 'Loading...' }} />;
  }

  if (query.length < minInputLength) {
    return (
      <ComboboxOptionContent
        option={{
          value: null,
          label: `Please type at least ${minInputLength} character${minInputLength === 1 ? '' : 's'}`,
        }}
      />
    );
  }

  if (options.length === 0) {
    return (
      <ComboboxOptionContent
        option={{
          value: null,
          label: `No results found for search term '${query}'`,
        }}
      />
    );
  }

  return (
    <>
      {options.map((option, index) =>
        renderCustomOption ? (
          <Fragment key={option.key ?? `option-${index}`}>{renderCustomOption(option)} </Fragment>
        ) : (
          <ComboboxOptionContent option={option} key={option.key ?? `option-${index}`} />
        ),
      )}
    </>
  );
};

const ComboboxOptionContent = ({
  option,
}: {
  option: ComboxboxFieldOption & { isSelected?: boolean; onClick?: () => void };
}) => (
  <ComboboxOption
    value={option}
    onClick={option.onClick}
    data-combobox-option-index-id={option.indexId}
    className={({ focus }) =>
      twx('relative cursor-default select-none py-2 pr-4 pl-4 text-content-subtle', {
        'bg-primary-background-hover': option.isSelected,
        'bg-content-subtle/10': focus && !option.isSelected,
      })
    }
  >
    {option.render ?? option.label}
  </ComboboxOption>
);

const findPositionInsideTree = (node: Node, position: number) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return { node, position: Math.min(Math.max(position, 0), node.textContent?.length ?? 0) };
  }

  let currentPosition = position;
  for (const child of node.childNodes) {
    if (currentPosition <= (child.textContent?.length ?? 0)) {
      return findPositionInsideTree(child, currentPosition);
    }

    currentPosition -= child.textContent?.length ?? 0;
  }

  return null;
};

interface SearchResult {
  humanIndex: number;
  sectionName: string;
  beforeText: string;
  matchText: string;
  afterText: string;
  indexId: string;
  position: {
    start: number;
    end: number;
  };
}

const isSearchResult = (value: unknown): value is SearchResult =>
  isObject(value) &&
  'sectionName' in value &&
  'beforeText' in value &&
  'matchText' in value &&
  'afterText' in value &&
  'indexId' in value &&
  'position' in value;

interface PerformDocumentSearchArgs {
  cdaDocument: CdaDocument;
  searchTerm: string;
}

const performDocumentSearch = ({
  cdaDocument,
  searchTerm,
}: PerformDocumentSearchArgs): Array<SearchResult> => {
  if (searchTerm === '') {
    return [];
  }

  // Create a case-insensitive regex pattern
  const pattern = new RegExp(escapeRegExp(searchTerm), 'gi');

  return cdaDocument.searchIndexes
    .flatMap((index) => {
      const matches = [...index.indexedText.matchAll(pattern)];

      return compact(
        matches.map((match) => {
          if (match.index === undefined) {
            return null;
          }

          const start = match.index;
          const end = start + searchTerm.length;

          const beforeText = `${index.indexedText.slice(Math.max(0, start - 40), start)}`;
          const matchText = index.indexedText.slice(start, end);
          const afterText = `${index.indexedText.slice(end, Math.min(end + 40, index.indexedText.length))}`;

          return {
            sectionName:
              cdaDocument.content?.sections.find((section) => section.id === index.sectionId)
                ?.title ?? 'Unknown Section',
            beforeText,
            matchText,
            afterText,
            humanIndex: 0, // Set later after flatmap
            indexId: index.indexId,
            position: {
              start,
              end,
            },
          };
        }),
      );
    })
    .map((result, index) => {
      result.humanIndex = index + 1;
      return result;
    });
};

const rangesForSearchResults = (searchResults: Array<SearchResult>) => {
  return compact(
    searchResults.map((result) => {
      const element = document.querySelector(`[data-search-index='${result.indexId}']`);
      if (element) {
        const range = new Range();
        const textStart = element.textContent?.indexOf(result.matchText) ?? -1;
        const textEnd = textStart + result.matchText.length;

        const start = findPositionInsideTree(element, textStart);
        const end = findPositionInsideTree(element, textEnd);

        if (start && end) {
          range.setStart(start.node, start.position);
          range.setEnd(end.node, end.position);

          return range;
        }
      }

      return undefined;
    }),
  );
};
